import { window } from "global";
import { useState, useEffect } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AccountLandingPageLayout from "components/Accounts/Layouts/AccountLandingPageLayout";
import CustomInput from "components/CustomInput";
import ButtonSuccess from "components/Buttons/ButtonSuccess";

import { useInitiateOauthLoginLazyQuery } from "@ugitgud/legos/queries/auth";
import AuthRedirect from "components/Accounts/Auth/AuthRedirect";
import { isValidEmail } from "lib/form-helpers";
import { parseQueryString } from "@ugg/shared/utils/url-query-params";
import { AppRoutes } from "@ugg/shared/routes/app-routes";

import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgLolDiscordIcon } from "@ugitgud/legos/ui/icon/SVG/LolDiscordIcon";
import { SvgBrandsGoogle } from "@ugitgud/legos/ui/icon/SVG/BrandsGoogle";
import { SvgBrandsRiot } from "@ugitgud/legos/ui/icon/SVG/BrandsRiot";

export const Signup = (props) => {
  const history = useHistory();
  const location = useLocation();
  const search = location.search.substring(1);
  const parsedQueryString = parseQueryString(search);

  const [form, setForm] = useState({
    email: { value: "", error: "" },
  });

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  const { email } = form;

  const onInputChange = (key, value) => {
    setForm({ ...form, [key]: { ...form[key], ...value } });
  };

  const verifyForm = () => {
    let emailField;
    if (email.value.trim().length === 0) {
      emailField = { ...email, error: "Email address is required" };
    } else if (!isValidEmail(email.value.trim())) {
      emailField = { ...email, error: "Invalid email address" };
    } else {
      emailField = { ...email, error: "" };
    }
    const newForm = { ...form, email: emailField };
    return { form: newForm, error: !!emailField.error };
  };

  const completeSignup = (e) => {
    e.preventDefault();
    const { form: newForm, error } = verifyForm();
    setForm(newForm);
    if (error) return;
    history.push({ pathname: AppRoutes.AUTH_CREATE, search, state: { verifiedEmail: email.value, fromEmailVerification: true } });
  };

  // Oauth Login
  const [redirectingProvider, setRedirectingProvider] = useState(null);
  const [initiateOauthLogin, oauthState] = useInitiateOauthLoginLazyQuery();

  useEffect(() => {
    if (oauthState.data?.initiateOauthLogin?.url) {
      window.location.href = oauthState.data.initiateOauthLogin.url;
    }
  }, [oauthState.data]);

  const handleOauthSignup = (providerName) => {
    setRedirectingProvider(providerName);
    sessionStorage.setItem("oauthLoginReturnTo", AppRoutes.SIGNUP);
    if (parsedQueryString.continue) {
      sessionStorage.setItem("oauthContinueTo", parsedQueryString.continue);
    } else {
      sessionStorage.removeItem("oauthContinueTo");
    }
    initiateOauthLogin({ variables: { providerName, redirectLocal: process.env.NODE_ENV === "development" } });
  };

  if (redirectingProvider) {
    return <AuthRedirect siteName={redirectingProvider.charAt(0).toUpperCase() + redirectingProvider.slice(1)} />;
  }

  return (
    <AccountLandingPageLayout>
      <Helmet>
        <title>U.GG - Signup</title>
      </Helmet>
      <>
        <div className="text-white text-[30px] font-bold font-barlow">Create an Account</div>
        <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">
          {parsedQueryString.continue?.startsWith(AppRoutes.CHECKOUT) ? "to Unlock U.GG PLUS" : "to Get Started"}
        </div>
        <form
          onSubmit={completeSignup}
          className="w-full xs:block flex flex-col min-h-[62vh] xs:min-h-0"
          style={{ textAlign: "left" }}
        >
          <CustomInput
            className="
                mb-[20px] [&_input]:h-[45px] [&_input]:w-full [&_input]:max-w-[300px]
                [&_input]:text-[16px] [&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0
                [&_input]:bg-[rgba(7,7,32,0.2)] xs:[&_input]:bg-[rgba(7,7,32,0.2)] [&_input]:pl-4 [&_input:focus]:outline-none [&_input:focus]:border-0
                [&_input]:placeholder:text-lavender-50 [&_input]:placeholder:font-normal
                [&_.input-label]:text-[16px] [&_.input-label]:mb-[10px] [&_.input-label]:text-lavender-50 [&_.input-label]:font-inter
                max-sm:[&_input]:bg-purple-100
              "
            value={email.value}
            error={email.error}
            onChange={(e) => onInputChange("email", { value: e.target.value })}
            placeholder="Enter your email address"
            type="email"
            tabIndex={1000}
            autoFocus
          />
          <ButtonSuccess
            className="btn-blue h-[42px] !text-[14px]"
            label="Continue"
            onClick={completeSignup}
            bForceSubmitOnEnter={true}
          />
          <div className="flex items-center my-[20px]">
            <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
            <span className="mx-2 text-[10px] text-lavender-50">OR</span>
            <div className="h-[1px] w-full max-w-[131px] bg-[#5c5c8e]" />
          </div>
          <div className="mb-[20px] flex justify-start">
            <button
              type="button"
              onClick={() => handleOauthSignup("riot")}
              className="bg-[#eb0029] rounded-[4px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
              aria-label="Signup with Riot"
            >
              <Icon icon={<SvgBrandsRiot />} className="w-[20px] h-[20px]" />
            </button>
            <button
              type="button"
              onClick={() => handleOauthSignup("google")}
              className="bg-[#f7f7f7] rounded-[4px] ml-[12px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
              aria-label="Signup with Google"
            >
              <Icon icon={<SvgBrandsGoogle />} className="w-[20px] h-[20px]" />
            </button>
            <button
              type="button"
              onClick={() => handleOauthSignup("discord")}
              className="bg-[#5865F2] rounded-[4px] ml-[12px] h-[36px] w-[92px] cursor-pointer flex items-center justify-center"
              aria-label="Signup with Discord"
            >
              <Icon icon={<SvgLolDiscordIcon />} className="w-[20px] h-[20px]" />
            </button>
          </div>
          <div className="text-lavender-50 text-[14px] font-inter mb-[24px] xs:mb-[48px]">
            Already have an account?{" "}
            <Link className="underline text-accent-blue-400 font-bold" to={{ pathname: AppRoutes.LOGIN, search }}>
              <span>Log In.</span>
            </Link>
          </div>
          <div className="text-accent-gray-200 text-[14px] font-inter">
            U.GG isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially
            involved in producing or managing League of Legends.
          </div>
        </form>
      </>
    </AccountLandingPageLayout>
  );
};

export default Signup;
